import { useMemo } from 'react';

import { CopyButton } from '@/components/CopyButton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getConfig } from '@/config';

export interface SnippetInputs {
  writeKey: string;
  /** Distinct id used in the curl example only. */
  sampleDistinctId?: string;
}

/**
 * Build the exact text of each install tab.
 *
 * Acceptance A3.2 is that the snippet contains the real production key and the
 * real SRI hash, with no placeholders anywhere. Both come from build time
 * configuration written by the SDK publish workflow, so there is nothing here
 * for a customer to fill in and nothing for us to forget to substitute.
 *
 * When the build was not given an exact published version, the snippet points
 * at the floating `/v1/` alias and carries no `integrity` attribute. That is
 * deliberate rather than a degraded mode: an integrity hash pinned to an alias
 * whose contents change by design would break every customer page on the next
 * SDK release. The snippet is still complete and still contains no placeholder.
 */
export function buildSnippets(inputs: SnippetInputs): {
  script: string;
  npm: string;
  curl: string;
} {
  const config = getConfig();
  const src = `${config.cdnHost}/${config.sdkPath}/zs.js`;
  const distinctId = inputs.sampleDistinctId ?? 'u_9f2c';

  const integrity =
    config.sdkSri === null
      ? ''
      : `\n  integrity="${config.sdkSri}"\n  crossorigin="anonymous"`;

  const script = `<script
  src="${src}"${integrity}
  defer
></script>
<script>
  window.zelisightQueue = window.zelisightQueue || [];
  window.addEventListener("load", function () {
    window.zelisight.init("${inputs.writeKey}", {
      api_host: "${config.ingestHost}"
    });
    window.zelisight.capture("app_loaded");
  });
</script>`;

  const npm = `npm install @zelibot/zelisight-js --registry=https://npm.pkg.github.com

import zs from "@zelibot/zelisight-js";

zs.init("${inputs.writeKey}", {
  api_host: "${config.ingestHost}"
});

zs.capture("app_loaded");`;

  const curl = `curl -sS -X POST '${config.ingestHost}/v1/e' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "write_key": "${inputs.writeKey}",
    "sent_at": "'"$(date -u +%Y-%m-%dT%H:%M:%S.000Z)"'",
    "sdk": { "n": "curl", "v": "1" },
    "events": [{
      "event_id": "01J8Z9K3M4N5P6Q7R8S9T0V1W2",
      "name": "server_test",
      "ts": "'"$(date -u +%Y-%m-%dT%H:%M:%S.000Z)"'",
      "distinct_id": "${distinctId}",
      "retry": 0,
      "props": { "source": "curl" }
    }]
  }' -w '%{http_code}\\n'
# expect: 202`;

  return { script, npm, curl };
}

function SnippetPane({ code, note }: { code: string; note: string }) {
  return (
    <div className="space-y-2">
      <div className="relative">
        <div className="absolute top-2 right-2 z-10">
          <CopyButton value={code} label="Copy" copiedLabel="Copied" />
        </div>
        <pre className="overflow-x-auto rounded-2xl border border-border bg-muted/50 p-4 pr-28 font-mono text-xs leading-relaxed">
          <code>{code}</code>
        </pre>
      </div>
      <p className="text-xs text-muted-foreground">{note}</p>
    </div>
  );
}

export function SnippetTabs({ writeKey }: { writeKey: string }) {
  const snippets = useMemo(() => buildSnippets({ writeKey }), [writeKey]);
  const pinned = getConfig().sdkSri !== null;

  return (
    <Tabs defaultValue="script">
      <TabsList>
        <TabsTrigger value="script">Script tag</TabsTrigger>
        <TabsTrigger value="npm">npm</TabsTrigger>
        <TabsTrigger value="curl">HTTP</TabsTrigger>
      </TabsList>

      <TabsContent value="script">
        <SnippetPane
          code={snippets.script}
          note={
            pinned
              ? 'Paste into your page head. The integrity hash pins this exact bundle, so a compromised CDN cannot serve you different code.'
              : 'Paste into your page head. This points at the v1 alias, which picks up patch releases automatically.'
          }
        />
      </TabsContent>

      <TabsContent value="npm">
        <SnippetPane
          code={snippets.npm}
          note="Install once, then call init as early as your app boots. The analytics entry does not pull session replay into your bundle."
        />
      </TabsContent>

      <TabsContent value="curl">
        <SnippetPane
          code={snippets.curl}
          note="The raw ingest contract. Useful from a server, a CI job or any language without an SDK. A valid batch answers 202 with an empty body."
        />
      </TabsContent>
    </Tabs>
  );
}
